// InlineEditField.jsx - Smart inline editing component
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  IconButton,
  ClickAwayListener,
  Select,
  MenuItem,
  FormControl,
  useTheme,
  alpha,
  Fade,
} from '@mui/material';
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

/**
 * InlineEditField - Unified inline editing component
 * Supports text, multiline, select, date, and number inputs
 */
const InlineEditField = ({
  value,
  onChange,
  placeholder = 'Click to edit',
  variant = 'text', // 'text', 'title', 'description', 'select', 'date', 'number'
  multiline = false,
  rows = 1,
  options = [], // For select variant
  disabled = false,
  error = false,
  autoFocus = false,
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const inputRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value || '');
  const [isHovered, setIsHovered] = useState(false);
  
  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);
  
  // Auto-focus when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      
      // Select all text for easier editing
      if (inputRef.current.select) {
        inputRef.current.select();
      }
    }
  }, [isEditing]);
  
  // Handle edit start
  const startEdit = () => {
    if (disabled) return;
    setIsEditing(true);
  };
  
  // Handle save
  const handleSave = () => {
    // Only save if the value has actually changed
    if (localValue !== value) {
      onChange(localValue);
    }
    setIsEditing(false);
  };
  
  // Handle cancel
  const handleCancel = () => {
    setLocalValue(value || '');
    setIsEditing(false);
  };
  
  // Handle key press
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      e.stopPropagation(); // Prevent event bubbling
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      handleCancel();
    }
  };
  
  // Get typography variant based on field variant
  const getTypographyVariant = () => {
    switch (variant) {
      case 'title':
        return 'h5';
      case 'description':
        return 'body1';
      default:
        return 'body2';
    }
  };
  
  // Get styles based on variant
  const getStyles = () => {
    const baseStyles = {
      cursor: disabled ? 'default' : 'pointer',
      borderRadius: 1,
      transition: 'all 0.2s ease',
      position: 'relative',
      ...sx,
    };
    
    if (variant === 'title') {
      return {
        ...baseStyles,
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.4,
      };
    }
    
    if (variant === 'description') {
      return {
        ...baseStyles,
        color: value ? 'text.primary' : 'text.secondary',
      };
    }
    
    return baseStyles;
  };
  
  // Render read-only view
  const renderReadOnly = () => {
    const displayValue = value || placeholder;
    const isEmpty = !value;
    
    return (
      <Box
        onClick={startEdit}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          ...getStyles(),
          p: variant === 'title' ? '8px 12px' : '8px 12px',
          minHeight: variant === 'title' ? 48 : 40,
          display: 'flex',
          alignItems: 'center',
          bgcolor: isHovered && !disabled ? alpha(theme.palette.primary.main, 0.04) : alpha(theme.palette.action.hover, 0.04),
          border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          '&:hover': !disabled && {
            borderColor: alpha(theme.palette.primary.main, 0.2),
            bgcolor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        <Typography
          variant={getTypographyVariant()}
          sx={{
            color: isEmpty ? 'text.secondary' : 'inherit',
            fontStyle: isEmpty ? 'italic' : 'normal',
            flex: 1,
          }}
        >
          {displayValue}
        </Typography>
        
        {/* Edit icon on hover */}
        {isHovered && !disabled && (
          <Fade in>
            <EditIcon
              sx={{
                ml: 1,
                fontSize: 16,
                color: 'text.secondary',
              }}
            />
          </Fade>
        )}
        
      </Box>
    );
  };
  
  // Render edit mode - only for text, number, and multiline variants
  const renderEditMode = () => {
    // Text, number, and multiline variants
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <TextField
          ref={inputRef}
          value={localValue}
          onChange={(e) => {
            const newValue = e.target.value;
            setLocalValue(newValue);
            // Immediately notify parent to enable auto-save
            if (onChange) {
              onChange(newValue);
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          fullWidth
          multiline={multiline}
          rows={rows}
          type={variant === 'number' ? 'number' : 'text'}
          size="small"
          error={error}
          autoFocus
          sx={{
            '& .MuiOutlinedInput-root': {
              minHeight: variant === 'title' ? 48 : 40,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.background.paper, 1),
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.2),
                },
              },
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 1,
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.divider, 0.12),
              },
            },
            '& .MuiInputBase-input': {
              fontSize: variant === 'title' ? '1.25rem' : 'inherit',
              fontWeight: variant === 'title' ? 600 : 'inherit',
              padding: '8px 12px',
              lineHeight: variant === 'title' ? 1.4 : 'inherit',
            },
          }}
        />
        
        
      </Box>
    );
  };
  
  // Main render
  // For select and date variants, always show them as interactive (no read-only mode)
  if (variant === 'select') {
    return (
      <Box sx={{ width: '100%', ...sx }}>
        <FormControl fullWidth size="small">
          <Select
            value={value || localValue || ''}
            onChange={(e) => {
              const newValue = e.target.value;
              setLocalValue(newValue);
              // Only call onChange if the value actually changed
              if (newValue !== value) {
                onChange(newValue);
              }
            }}
            disabled={disabled}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 0.5,
                  '& .MuiMenuItem-root': {
                    fontSize: 'inherit',
                  },
                },
              },
            }}
            sx={{
              minHeight: 40,
              borderRadius: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 'inherit',
                bgcolor: alpha(theme.palette.action.hover, 0.04),
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                  },
                },
                '&.Mui-focused': {
                  bgcolor: alpha(theme.palette.background.paper, 1),
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 1,
                  },
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.divider, 0.12),
                borderRadius: 'inherit',
              },
              '& .MuiSelect-select': {
                padding: '8px 12px',
                fontSize: 'inherit',
                fontWeight: 'inherit',
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiSelect-icon': {
                right: 8,
              },
            }}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  }
  
  if (variant === 'date') {
    return (
      <Box sx={{ width: '100%', ...sx }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            value={value || localValue}
            onChange={(newValue) => {
              setLocalValue(newValue);
              // Only call onChange if the value actually changed
              if (newValue?.getTime() !== value?.getTime()) {
                onChange(newValue);
              }
            }}
            disabled={disabled}
            renderInput={(params) => (
              <TextField 
                {...params} 
                size="small" 
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    minHeight: 40,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.action.hover, 0.04),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                      },
                    },
                    '&.Mui-focused': {
                      bgcolor: alpha(theme.palette.background.paper, 1),
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 1,
                      },
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(theme.palette.divider, 0.12),
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 12px',
                    fontSize: 'inherit',
                  },
                }}
              />
            )}
          />
        </LocalizationProvider>
      </Box>
    );
  }
  
  if (isEditing) {
    return (
      <ClickAwayListener onClickAway={handleSave}>
        <Box sx={sx}>
          {renderEditMode()}
        </Box>
      </ClickAwayListener>
    );
  }
  
  return renderReadOnly();
};

export default InlineEditField;